// Decides where a clicked link should open. Kept free of Vuetify/Capacitor
// imports so it stays unit-testable in the node test environment.

import { EXTERNAL_LINK_PROTOCOLS } from '@/constants/inAppBrowser'

// What should happen to a clicked link.
export const LINK_TARGET = {
  IGNORE: 'ignore', // not ours — let the browser/OS handle it
  INTERNAL: 'internal', // same origin — route in-app instead of reloading the SPA
  EXTERNAL: 'external', // another origin — open the in-app browser
}

const ignore = () => ({ target: LINK_TARGET.IGNORE, href: null, path: null })

/**
 * Decide how a link should be opened.
 *
 * @param {string} href       the anchor's raw href (may be relative)
 * @param {string} currentHref the document's current URL, used as the base
 * @returns {{ target: string, href: string|null, path: string|null }}
 *          `path` is only set for INTERNAL links, ready for router.push.
 */
export function classifyLinkHref(href, currentHref) {
  const raw = typeof href === 'string' ? href.trim() : ''
  // A bare fragment is an in-page jump — resolving it would look same-origin
  // and wrongly trigger a route push.
  if (!raw || raw.startsWith('#')) return ignore()

  let url
  let base
  try {
    url = new URL(raw, currentHref)
    base = new URL(currentHref)
  } catch {
    return ignore()
  }

  if (!EXTERNAL_LINK_PROTOCOLS.includes(url.protocol)) return ignore()

  if (url.origin === base.origin) {
    return {
      target: LINK_TARGET.INTERNAL,
      href: url.href,
      path: `${url.pathname}${url.search}${url.hash}`,
    }
  }

  return { target: LINK_TARGET.EXTERNAL, href: url.href, path: null }
}
